import { buildSiteHierarchyGroups, type SiteTreeMeta } from '@/lib/catalogSiteTree';
import {
  defaultMiniproFeatures,
  normalizeMiniproFeatures,
  type MiniproFeatures,
} from '@/lib/miniproFeatures';

export type MiniproFeaturesSite = SiteTreeMeta;

export function expandFeaturesSiteIds(siteIds: number[], allSites: MiniproFeaturesSite[]): Set<number> {
  const byParent = new Map<number, MiniproFeaturesSite[]>();
  for (const s of allSites) {
    const pid = s.parent_site_id != null && s.parent_site_id > 0 ? s.parent_site_id : 0;
    if (pid > 0) {
      const list = byParent.get(pid) ?? [];
      list.push(s);
      byParent.set(pid, list);
    }
  }
  const acc = new Set<number>();
  for (const id of siteIds) {
    if (id > 0) acc.add(id);
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of [...acc]) {
      for (const c of byParent.get(id) ?? []) {
        if (!acc.has(c.id)) {
          acc.add(c.id);
          changed = true;
        }
      }
    }
  }
  return acc;
}

export function featuresSitesInScope(siteIds: number[], allSites: MiniproFeaturesSite[]): MiniproFeaturesSite[] {
  const scope = expandFeaturesSiteIds(siteIds, allSites);
  return allSites.filter((s) => scope.has(s.id));
}

export function buildFeaturesHierarchyGroups(siteIds: number[], allSites: MiniproFeaturesSite[]) {
  return buildSiteHierarchyGroups(featuresSitesInScope(siteIds, allSites));
}

export function hasFeaturesOverride(
  pages: Record<string, MiniproFeatures> | undefined,
  siteId: number,
): boolean {
  return Object.prototype.hasOwnProperty.call(pages ?? {}, String(siteId));
}

function mergeFeatures(parent: MiniproFeatures, own: MiniproFeatures): MiniproFeatures {
  return normalizeMiniproFeatures({
    home_stats: own.home_stats ?? parent.home_stats,
    float_actions: own.float_actions ?? parent.float_actions,
    home_ads: own.home_ads ?? parent.home_ads,
    dial_bubble: own.dial_bubble ?? parent.dial_bubble,
    auto_dial: own.auto_dial ?? parent.auto_dial,
    publisher_ads: own.publisher_ads ?? parent.publisher_ads,
  });
}

/** 二级无单独配置时沿用一级；一级无单独配置时沿用全站默认 */
export function effectiveSiteFeatures(
  global: MiniproFeatures,
  pages: Record<string, MiniproFeatures> | undefined,
  siteId: number,
  sitesById: Map<number, MiniproFeaturesSite>,
): MiniproFeatures {
  const map = pages ?? {};
  const site = sitesById.get(siteId);
  const pid = site?.parent_site_id != null && site.parent_site_id > 0 ? site.parent_site_id : 0;
  if (pid > 0) {
    const parentEff = effectiveSiteFeatures(global, map, pid, sitesById);
    if (!hasFeaturesOverride(map, siteId)) {
      return parentEff;
    }
    const own = normalizeMiniproFeatures(map[String(siteId)], { forEdit: true });
    return mergeFeatures(parentEff, own);
  }
  if (!hasFeaturesOverride(map, siteId)) {
    return global;
  }
  const own = normalizeMiniproFeatures(map[String(siteId)], { forEdit: true });
  return mergeFeatures(global, own);
}

export function featuresEnabledSummary(cfg: MiniproFeatures): string {
  const parts: string[] = [];
  if (cfg.home_stats.enabled) parts.push('统计');
  if (cfg.float_actions.enabled) parts.push('悬浮钮');
  if (cfg.home_ads.enabled) parts.push('广告');
  if (cfg.dial_bubble.enabled) parts.push('拨号气泡');
  if (cfg.auto_dial.enabled) parts.push('自动拨号');
  if (cfg.publisher_ads.enabled) parts.push('流量主');
  return parts.length ? parts.join(' · ') : '沿用上级（均未单独开启）';
}
